// frontend/src/components/ProjectDetails.js

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  IconButton, 
  TextField,
  Button,
  Container
} from '@mui/material';
import NavBar from './NavBar';
import SurveyBuilder from './SurveyBuilder';
import ShareProject from './ShareProject';
import MonitorProject from './MonitorProject';
import AnalyzeProject from './AnalyzeProject';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

function ProjectDetails() {
  const { id: projectId } = useParams();
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState('create');
  const [error, setError] = useState(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/projects/${projectId}`)
      .then(response => {
        setProject(response.data);
      })
      .catch(error => {
        console.error(error);
        setError('Failed to load project details.');
      });
  }, [projectId]);

  const handlePreview = () => {
    window.open(`/survey/${projectId}?preview=true`, '_blank');
  };

  const handleTitleChange = (e) => {
    setProject({ ...project, name: e.target.value });
  };

  const handleTitleBlur = () => {
    setIsEditingTitle(false);
    api.put(`/projects/${projectId}`, { name: project.name })
      .catch(error => {
        console.error('Failed to update project title:', error);
        setError('Failed to update project title. Please try again.');
      });
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'create':
        return <SurveyBuilder projectId={projectId} />;
      case 'share':
        return <ShareProject projectId={projectId} />;
      case 'monitor':
        return <MonitorProject projectId={projectId} />;
      case 'analyze':
        return <AnalyzeProject projectId={projectId} />;
      default:
        return null;
    }
  };

  if (error) return (
    <Box sx={{ p: 3 }}>
      <Typography color="error">{error}</Typography>
    </Box>
  );
  
  if (!project) return (
    <Box sx={{ p: 3 }}>
      <Typography>Loading...</Typography>
    </Box>
  );

  return (
    <Box sx={{ 
      bgcolor: 'rgb(250, 250, 250)',
      minHeight: '100vh',
      width: '100%'
    }}>
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 0,
          boxShadow: 'none',
          position: 'relative',
          bgcolor: 'white',
          p: 0
        }}
      >
        <Box sx={{ 
          p: '32px 48px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          minHeight: '56px',
          width: '100%',
          margin: '0 auto',
          gap: '20px'
        }}>
          <Box sx={{ 
            flexGrow: 1, 
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            {isEditingTitle ? (
              <TextField
                fullWidth
                variant="standard"
                value={project.name}
                onChange={handleTitleChange}
                onBlur={handleTitleBlur}
                onKeyDown={(e) => e.key === 'Enter' && handleTitleBlur()}
                autoFocus
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    padding: '0'
                  }
                }}
              />
            ) : (
              <Typography 
                variant="h5" 
                onClick={() => setIsEditingTitle(true)}
                sx={{ 
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1.5rem'
                }}
              >
                {project.name}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            onClick={handlePreview}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: '#0056b3'
              }
            }}
          >
            Preview Survey
          </Button>
        </Box>
      </Paper>

      <NavBar 
        projectId={projectId} 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
      />

      <Box sx={{ mt: 3, width: '100%' }}>
        {renderContent()}
      </Box>
    </Box>
  );
}

export default ProjectDetails;
